import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {Entity} from "./entity";
import {validateJSON, validateJSONNow} from "../web/validation";

export const VehicleValidator = t.intersection([
  t.type({
    id: t.string,
    internalId: t.string,
    companyId: t.string,
    doNotUse: t.boolean
  }),
  t.partial({
    barcode: t.string,
    type: t.string,
    year: t.number,
    model: t.string,
    make: t.boolean,
    color: t.string,
    vin: t.string,
    licenseNumber: t.string,
    licenseState: t.string,
    assignedPersonId: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    database: t.string,
    /* extras */
    companyName: t.string,
    companyType: t.string,
    assignedPersonName: t.string,
    regionList: t.array(t.string),
    lastUsedDttm: t.number
  })
]);

export const VehicleArrayValidator = t.array(VehicleValidator);

export type Vehicle = t.TypeOf<typeof VehicleValidator>;
export type VehicleArray = t.TypeOf<typeof VehicleArrayValidator>;

/**
 * A vehicle.
 */
@Exclude()
export class VehicleEntity implements Entity {
  @Expose()
  id!: string;

  @Expose({name: "internalId"})
  name!: string;

  @Expose()
  barcode?: string

  @Expose()
  companyId!: string;

  @Expose()
  companyName?: string;

  @Expose()
  companyType?: string;

  @Expose()
  type?: string;

  @Expose()
  year?: number;

  @Expose()
  model?: string;

  @Expose()
  make?: string;

  @Expose()
  color?: string;

  @Expose()
  vin?: string;

  @Expose()
  licenseNumber?: string;

  @Expose()
  licenseState?: string;

  @Expose()
  assignedPersonId?: string;

  @Expose()
  assignedPersonName?: string;

  @Expose({name: "regionList"})
  regionIds?: string[];

  @Expose()
  doNotUse = false;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  readonly database?: string;

  readonly entityType = "VEHICLE";

  get displayName(): Readonly<string> {
    let name = this.name;
    if (this.companyName) {
      name += ` (${this.companyName})`;
    }
    return name;
  }

  /**
   * Constructor for VehicleEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(vehicle: Vehicle): VehicleEntity {
    return plainToInstance(VehicleEntity, vehicle, {
      excludeExtraneousValues: true
    });
  }

  loadTypeValue(): Promise<Vehicle> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(VehicleValidator, object);
  }

  asTypeValue(): Vehicle {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(VehicleValidator, object);
  }
}
